import { z } from 'zod';

const ZeroOneSchema = z.union([z.literal('0'), z.literal('1')]).nullable();

export const ItemShipmentInfoSchema = z.object({
  id: z.string(),
  deliveryLeg: z.string(),
  hasShipmentPickupEvent: z.boolean(),
  shippingLabelUrl: z.string().nullish(),
  podLabelUrl: z.string().nullish(),
  tmsShipmentId: z.string().nullish(),
  shipmentType: z.string().nullish(),
});
export type ItemShipmentInfo = z.infer<typeof ItemShipmentInfoSchema>;

export const ItemSellerPayoutSchema = z.object({
  lastStatusDate: z.string(),
  transferMethod: z.string(),
  overallPayoutStatus: z.string(),
  splitPaymentStatus: z.string(),
  splitPaymentStatuses: z.string().array().nullish(),
  userPayoutCurrency: z.string(),
  userCurrencyAmount: z.string(),
  failedReason: z.string().nullish(),
  bankName: z.string().nullish(),
  accountDetail: z.string().nullish(),
  accountType: z.string().nullish(),
  payoutId: z.number().nullish().optional(),
  internalPayoutStatus: z.string().nullish().optional(),
});
export type ItemSellerPayout = z.infer<typeof ItemSellerPayoutSchema>;

export const DecisionSchema = z.object({
  createdAt: z.string().nullish(),
  decisionType: z.string().nullish(),
  decisionValue: z.string().nullish(),
  label: z.string().nullish(),
});

export const LitigationSchema = z.object({
  litigationNumbers: z.string().array().nullish(),
  daysInProgress: z.number().nullish(),
  startedAt: z.string().nullish(),
  concludedAt: z.string().nullish(),
  decisions: DecisionSchema.array().nullish(),
  workflowStatus: z.string().nullish(),
});

export const ItemPostalLitigationSchema = z.object({
  litigations: LitigationSchema.array().nullish(),
});

export type ItemPostalLitigation = z.infer<typeof ItemPostalLitigationSchema>;

export const ItemFlagSchema = z.object({ id: z.string(), name: z.string() });
export type ItemFlag = z.infer<typeof ItemFlagSchema>;

export const ItemRefundSchema = z.object({
  itemId: z.string(),
  createdAt: z.string(),
  type: z.string(),
  amount: z.string(),
  comment: z.string(),
  status: z.string(),
  internalComment: z.string(),
  isApproved: z.boolean(),
  state: z.number(), // STATE_REFUND_TO_DO = 0; STATE_REFUND_BEING_PROCESS = 3; STATE_REFUND_SUCCESS = 1; STATE_REFUND_FAILED = 2; STATE_REFUND_ON_HOLD = 4;
  refundAccount: z.string().nullish(),
  failedReason: z.string().nullish(),
  executedAt: z.string().nullish(),
});
export type ItemRefund = z.infer<typeof ItemRefundSchema>;

const sellerShippingAddressSchema = z.object({
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  companyName: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  district: z.string().nullable(),
  city: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string().nullable(),
  countryIso2: z.string().nullable(),
  province: z.string().nullable(),
});
const sellerAskInfoSchema = z.object({
  statusId: z.number().nullable(),
});
const countriesOfOriginSchema = z.object({
  tentativeCoo: z.string().nullable(),
  actualCoo: z.string().nullable(),
});

export type SellerShippingAddress = z.infer<typeof sellerShippingAddressSchema>;

export const ItemSchema = z.object({
  productId: z.number(),
  productName: z.string().nullable(),
  brandName: z.string(),
  price: z.string(),
  netSellingPrice: z.string(),
  initialPrice: z.string(),
  commission: z.string(),
  productLastAction: z.string(),
  isTradesyMigrated: ZeroOneSchema,
  isSold: ZeroOneSchema,
  soldDate: z.string().nullable(),
  buyerPaymentMethod: z.string().nullable(),
  estimatedDeliveryDate: z.string().nullable(),
  shippingMethod: z.string().nullable(),
  orderConfirmationDate: z.string().nullable(),
  shipmentInfo: ItemShipmentInfoSchema,
  isShipmentSponsored: z.boolean().nullable(),
  isPastShippingWindow: z.boolean().nullable(),
  enableCancelPurchase: z.boolean(),
  enableConfirmDelivered: z.boolean().optional(),
  enableDownloadShippingLabel: z.boolean(),
  carrierName: z.string().nullable(),
  trackingNumber: z.string(),
  trackingLink: z.string(),
  warehouseName: z.string(),
  dateReceived: z.string().nullable(),
  neverReceived: ZeroOneSchema,
  dateReceivedAtWareHouse: z.string().nullish(),
  isLost: ZeroOneSchema,
  dateLost: z.string(),
  phoneCountry: z.string().nullish(),
  state: z.string().nullish(),
  complianceDate: z.string(),
  isNonCompliance: ZeroOneSchema,
  counterfeit: ZeroOneSchema,
  productSellerPayout: ItemSellerPayoutSchema,
  isSellerTransferMade: ZeroOneSchema,
  sellerTransferDade: z.string(),
  isTransferBlocked: ZeroOneSchema,
  isBlockedByLitigation: ZeroOneSchema,
  eligibleForReturn: z.boolean().nullable(),
  estimatedSellerPaymentDate: z.string().nullable(),
  internalComments: z.string(),
  sellerId: z.string(),
  buyerId: z.string().nullable(),
  orderId: z.string().nullable(),
  unlockReturn: z.boolean(),
  flags: ItemFlagSchema.array().optional(),
  sectionName: z.string(),
  model: z.string(),
  brandId: z.number(),
  sectionId: z.number(),
  modelId: z.number(),
  previewLink: z.string().nullish(),
  language: z.string().optional(),
  description: z.string(),
  descriptionDe: z.string().nullish(),
  descriptionEn: z.string().nullish(),
  descriptionEs: z.string().nullish(),
  descriptionFr: z.string().nullish(),
  descriptionIt: z.string().nullish(),
  descriptionKo: z.string().nullish(),
  descriptionHk: z.string().nullish(),
  descriptionNl: z.string().nullish(),
  descriptionSv: z.string().nullish(),
  pickingRequestDate: z.string().optional(),
  inStock: z.boolean().optional(),
  formerVcRef: z.number().nullable(),
  newVcRef: z.union([z.string(), z.number()]).array().nullable(),
  pickingRequestNumber: z.string().nullable(),
  disableReturnToSeller: z.boolean(),
  condition: z.string(),
  conditionId: z.number(),
  material: z.string(),
  materialId: z.number(),
  color: z.string(),
  colorId: z.number(),
  size: z.string(),
  postalLitigation: ItemPostalLitigationSchema.nullish().optional(),
  refunds: ItemRefundSchema.array(),
  negoComment: z.string(),
  negoDetail: z.record(z.string()).array().nullable(),
  returnInternalComment: z.string().nullable(),
  QCAgentInternalComment: z.string().nullable(),
  negoType: z.string().nullable(),
  negoStatus: z.string().nullable(),
  negoDefectDetails: z
    .array(
      z.object({
        defectName: z.string(),
        defectComment: z.string().nullable(),
      }),
    )
    .optional(),
  isLitigation: z.boolean().optional(),
  litigationCloseDate: z.string().optional(),
  litigationCreationDate: z.string().optional(),
  litigationTicketId: z.string().optional(),
  daysInProgress: z.number().optional(),
  enableRelistToBuyer: z.boolean(),
  isReserved: z.boolean().optional(),
  imageUrl: z.string(),
  enableLostInTransit: z.boolean(),
  enableWithdrawFromSale: z.boolean().nullish(),
  isAutoCancelSale: z.boolean(),
  interceptionDate: z.string().nullish().optional(),
  shippingPlanId: z.number().nullish().optional(),
  isDelivered: z.boolean().nullish().optional(),
  percentageMargin: z.string().nullish().optional(),
  listingCurationDetails: z.string().nullish().optional(),
  buyerCountryIso: z.string().nullish().optional(),
  paymentFailedOrders: z.union([z.string(), z.number()]).array().nullable().optional(),
  sellerShippingAddress: sellerShippingAddressSchema.nullish().optional(),
  /**
   * @deprecated
   * use `paymentCancelled`
   */
  orderPaymentStatus: z.string().nullish().optional(),
  paymentCancelled: z.boolean().nullish().optional(),
  productUnavailabilityReasonStatus: z.string().nullish().optional(),
  payByLinkStatus: z.string().nullish().optional(),
  packagings: z.array(z.string()).nullish().optional(),
  sellerAskInfo: sellerAskInfoSchema.nullish().optional(),
  countriesOfOrigin: countriesOfOriginSchema.nullish().optional(),
});

export type Item = z.infer<typeof ItemSchema>;

export const ItemHistoSchema = z.object({
  date: z.string(),
  sellingPrice: z.string(),
  sellerReceives: z.string(),
  sellingPriceEur: z.string().optional(),
  sellerReceivesEur: z.string().optional(),
  action: z.string(),
  operator: z.string().nullable(),
  warehouse: z.string(),
  stockLocation: z.string(),
  trackingLink: z.string(),
  info: z.string(),
});
export type ItemHisto = z.infer<typeof ItemHistoSchema>;

export const ClientItemSchema = z.object({
  productId: z.number(),
  productName: z.string(),
  brandName: z.string(),
  imageUrl: z.string(),
  soldDate: z.string().nullish(),
});
export type ClientItem = z.infer<typeof ClientItemSchema>;

export const ClientItemsSchema = z.object({
  hasMore: z.boolean(),
  products: ClientItemSchema.array(),
});
export type ClientItems = z.infer<typeof ClientItemsSchema>;

export enum TimelineType {
  SELLER = 'seller',
  BUYER = 'buyer',
}

export type ItemAttrPath = 'materials' | 'sizes' | 'colors' | 'conditions';
export const ItemAttrSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type ItemAttr = z.infer<typeof ItemAttrSchema>;

export type ItemReturnReasonFilter = 'warehouse' | 'relist';
export const ItemReturnReasonSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type ItemReturnReason = z.infer<typeof ItemReturnReasonSchema>;

export const ItemReturnReferenceSchema = z.object({
  pickingId: z.number().nullish().optional(),
});
export type ItemReturnReference = z.infer<typeof ItemReturnReferenceSchema>;

export type ItemRelistType = 'seller' | 'buyer' | 'vdc+' | 'vcny' | 'marketing' | 'sellerNonRecu';

export const NewProductIdSchema = z.object({
  newProductId: z.number(),
});
export type NewProductId = z.infer<typeof NewProductIdSchema>;

// what's wrong with API? why use NewProductIdSchema NewIdProductSchema ????
export const NewIdProductSchema = z.object({
  newIdProduct: z.number(),
});
export type NewIdProduct = z.infer<typeof NewIdProductSchema>;

export const ItemRelistPriceSchema = z.object({
  withRelistFee: z.object({
    publicPrice: z.string(),
    sellerPrice: z.string(),
    fee: z.string(),
  }),
  noRelistFee: z.object({
    publicPrice: z.string(),
    sellerPrice: z.string(),
    fee: z.string(),
  }),
});
export type ItemRelistPrice = z.infer<typeof ItemRelistPriceSchema>;

export const ItemCurrencySchema = z.object({
  code: z.string(),
  decimal_digits: z.number(),
});
export type ItemCurrency = z.infer<typeof ItemCurrencySchema>;

export const ItemMmaoSchema = z.object({
  id: z.number(),
  buyerEmail: z.string().nullable().optional(),
  status: z.string(),
  offerPrice: z.number().nullish(),
  offeredDate: z.string().nullable().optional(),
  closedDate: z.string().nullable().optional(),
  buyerFormattedOfferPrice: z.string().nullish(),
  sellerFormattedOfferPrice: z.string().nullish(),
  sellerFormattedEarningAmount: z.string().nullish(),
});

export type ItemMmao = z.infer<typeof ItemMmaoSchema>;

export const ItemPhotoSchema = z.object({
  id: z.string(),
  photoName: z.string(),
  order: z.string(),
});
export type ItemPhoto = z.infer<typeof ItemPhotoSchema>;

export const ReturnLitigationReasonSchema = z.object({
  value: z.string().nullish().optional(),
  displayText: z.string().nullish().optional(),
  displayWarning: z.string().nullish().optional(),
});
export type ReturnLitigationReason = z.infer<typeof ReturnLitigationReasonSchema>;
export const PrefillURFData = z.object({
  reason: z.string(),
  itemReceived: z.boolean(),
  isSellerWorkflow: z.boolean().nullish().optional(),
});
export const ItemCreateReturnLitigationSchema = z.object({
  canAccessReturnForm: z.boolean().nullish().optional(),
  zendeskTicketId: z.string().nullish().optional(),
  litigationDecision: z.string().nullish().optional(),
  zendeskTicketStatus: z.string().nullish().optional(),
  zendeskTicketLink: z.string().nullish().optional(),
  itemReceived: ReturnLitigationReasonSchema.array().nullish().optional(),
  itemNotReceived: ReturnLitigationReasonSchema.array().nullish().optional(),
  itemReceivedWithinPeriod: ReturnLitigationReasonSchema.array().nullish().optional(),
  itemReceivedExceedPeriod: ReturnLitigationReasonSchema.array().nullish().optional(),
  itemNotReceivedWithinPeriod: ReturnLitigationReasonSchema.array().nullish().optional(),
  itemNotReceivedExceedPeriod: ReturnLitigationReasonSchema.array().nullish().optional(),
  prefillData: PrefillURFData.nullish().optional(),
});
export type ItemCreateReturnLitigation = z.infer<typeof ItemCreateReturnLitigationSchema>;

export const ItemReturnFormLinkSchema = z.object({
  link: z.string().nullish().optional(),
});
export type ItemReturnFormLink = z.infer<typeof ItemReturnFormLinkSchema>;

export const ItemRequireAuthenticationSchema = z.object({
  authenticationRequired: z.boolean().nullish().optional(),
});
export type ItemRequireAuthentication = z.infer<typeof ItemRequireAuthenticationSchema>;

export type TrackingComment =
  | 'download_product_shipping_label'
  | 'download_product_proof_of_delivery'
  | 'view_seller_auth_qc_report'
  | 'view_buyer_auth_qc_report'
  | 'view_shipping_status_tracking'
  | 'product_preview'
  | 'view_action_histo'
  | 'view_cs_score_history'
  | 'talone_one_coupon_finder'
  | 'upload_seller_invoice'
  | 'update_shipping_status_warehouse';

export type ActionLogType = 'product' | 'order' | 'customer';

export const ItemActionLogSchema = z.object({
  id: z.number(),
  createdAt: z.string(),
  createdBy: z.string(),
  action: z.string(),
  comment: z.string().nullable(),
  updatedAt: z.string().nullable(),
  productId: z.number().nullable(),
  orderId: z.number().nullable(),
  customerId: z.number().nullable(),
  jsonData: z.string().nullish(),
  couponCode: z.string().nullish(),
});

export type ItemActionLog = z.infer<typeof ItemActionLogSchema>;

export const CancelReasonSchema = z.object({
  id: z.number(),
  reason: z.string(),
});

export type CancelReason = z.infer<typeof CancelReasonSchema>;

export const CarrierResponseSchema = z.object({
  name: z.string(),
  iconUrl: z.string(),
});
export type CarrierResponse = z.infer<typeof CarrierResponseSchema>;

export type PostalType = 'confirmation' | 'conclusion';

export enum EPaymentMethod {
  APPLE_PAY = 'Apple Pay',
  GOOGLE_PAY = 'Google Pay',
}

export const DecisionValueSchema = z.object({
  decisionValue: z.string().nullish(),
  label: z.string().nullish(),
  needRefundComment: z.boolean().nullish().optional(),
});

export const PLDecisionResponseSchema = z.object({
  type: z.string().nullish(),
  label: z.string().nullish(),
  decisionValues: DecisionValueSchema.array().nullish(),
});

export type PLDecisionResponse = z.infer<typeof PLDecisionResponseSchema>;

export enum AgentRole {
  BUYER = 'buyer',
  SELLER = 'seller',
}

export const PayByLinkDetailSchema = z.object({
  status: z.string().nullable(),
  detailedStatus: z.string().nullable(),
  paymentLink: z.string().nullable(),
  createdAt: z.string().nullable(),
  expiredAt: z.string().nullable(),
  reason: z.string().nullable(),
  formattedAmount: z.string().nullable(),
});

export type PayByLinkDetail = z.infer<typeof PayByLinkDetailSchema>;

export const PayByLinkFormSchema = z.array(
  z.object({
    type: z.string().nullish(),
    clientId: z.string().nullish(),
    reason: z.string().nullish(),
    reasonDisplayText: z.string().nullish(),
    currency: z.string().nullish(),
    email: z.string().nullish(),
    country: z.string().nullish(),
    amount: z.number().nullish(),
  }),
);

export type PayByLinkForm = z.infer<typeof PayByLinkFormSchema>;

export const PayoutStatusDetailSchema = z.object({
  data: z.array(
    z.object({
      productId: z.number(),
      amount: z.string(),
      currency: z.string(),
    }),
  ),
});
export type PayoutStatusDetail = z.infer<typeof PayoutStatusDetailSchema>;

/**
 * Direction of the shipment a label is requested for.
 *
 * - `forward`: shipping from seller to buyer
 * - `return`: shipping from buyer to seller
 */
export type ShipmentDirection = 'forward' | 'return';

export const ShippingLabelCarrierSchema = z.object({
  name: z.string(),
  logo: z.string(),
  shipmentLabelTypes: z.array(
    z.object({
      type: z.enum(['printable_label', 'shipping_code']),
      selected: z.boolean(),
    }),
  ),
  dropOffTypes: z.array(
    z.object({
      type: z.enum(['home_collection', 'drop_off']),
      selected: z.boolean(),
    }),
  ),
  selected: z.boolean(),
});

export type ShippingLabelCarrier = z.infer<typeof ShippingLabelCarrierSchema>;

export type ShippingReminder = {
  buyer?: {
    isReminderD1Sent: boolean;
    isReminderD2Sent: boolean;
    isReminderD3Sent: boolean;
    isReminderD4Sent: boolean;
    isReminderD5Sent: boolean;
    isReminderD6Sent: boolean;
  };
  seller?: {
    timeLeftToShip: string;
    timeToAutoCancellation: string;
    isReminderD1Sent: boolean;
    isReminderD2Sent: boolean;
    isReminderD3Sent: boolean;
    isReminderD4Sent: boolean;
    isReminderD5Sent: boolean;
    isReminderD6Sent: boolean;
  };
};

export type RestartOmsWorkflow = {
  workflowId: string;
};

export type PurchasePlace = {
  id: number;
  name: string;
};

export type AuthenticationAskInfo = {
  askSellerInfoStatus: 'asked' | 'not_answered';
  askSellerInfoReason: string;
};

export type SellerShippingStatus = {
  isAutoCancelledByScanLatency?: boolean;
  status: string;
  cancelType: string;
  expectedTimeoutCancellationDate: string;
  shipmentPickUpDate: string;
  isShippedWithinDays?: boolean;
};
